import { PageLoader } from '@customerio/pluma-components/react';
import { createFileRoute } from '@tanstack/react-router';
import { queryClient } from '@/services/query-client';
import { session } from '@/services/session';
import { snackbarApi } from '@/services/snackbar';
import { deleteCrossAppCacheByPrefix } from '@/utils/cross-app-cache';
import { confirmAccountExit, confirmAccountFromLink } from './-confirm-account';
import { authKeys } from '@/services/auth/query-keys';

async function confirmCode(confirmationCode: string) {
  // Loaded on demand: the signup module would otherwise ride along in the entry chunk with every route.
  const { confirmAccount } = await import('@/services/signup/api');
  await confirmAccount(confirmationCode);
}

/** The session loaded the account before it was confirmed, so a signed-in visitor would land on that copy. */
async function refreshCurrentAccount() {
  deleteCrossAppCacheByPrefix('/accounts');
  deleteCrossAppCacheByPrefix('/account_by_environment');
  await queryClient.invalidateQueries({ queryKey: authKeys.authenticated() });
}

export const Route = createFileRoute('/confirm_account')({
  // The code comes from the browser, not the router: the router parses each search value as JSON,
  // so its copy of a hex code that reads as a number (`12e45`) is already changed.
  beforeLoad: async () => {
    // Whether the visitor is signed in decides the copy and where they go next.
    await session.initialized.catch(() => undefined);
    const isAuthenticated = session.isAuthenticated.get();
    const snackbar = await confirmAccountFromLink({
      confirmationCode: new URLSearchParams(window.location.search).get('confirmationCode'),
      isAuthenticated,
      confirm: async (confirmationCode) => {
        await confirmCode(confirmationCode);
        if (isAuthenticated) {
          await refreshCurrentAccount();
        }
      },
    });
    snackbarApi.create(snackbar);
    throw confirmAccountExit(isAuthenticated);
  },
  pendingComponent: PageLoader,
});
